import { gql, useMutation, useQuery } from '@apollo/client'
import { Paper, Skeleton, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { Query, CustomField, Mutation } from '@promoboxx/graphql-gateway-types'
import { Field, Form, Formik } from 'formik'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useMemo } from 'react'

import CustomFieldField, { CustomFieldFieldProps } from './CustomFieldField'
import { useManageBrandDialogContext } from './manageBrandsDialogContext'

interface CustomFieldsProps {
  retailerId: string | number
  brandId?: string | number
  retailerAccountId: string | number
  isInBusinessSettings?: boolean
}

const ManageBrandCustomFields: React.FC<
  React.PropsWithChildren<CustomFieldsProps>
> = ({ retailerId, brandId, retailerAccountId, isInBusinessSettings }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [updateCustomFieldValue] = useMutation<Mutation>(
    UPDATE_CUSTOM_FIELD_VALUE,
  )
  const [createCustomFieldValue] = useMutation<Mutation>(
    CREATE_CUSTOM_FIELD_VALUE,
  )

  const { data, loading } = useQuery<Query>(GET_CUSTOM_FIELDS, {
    variables: {
      params: {
        resourceId: brandId,
        resourceType: 'brands',
      },
      fieldsParams: {
        resourceId: brandId,
        resourceType: 'brands',
      },
      fieldValueParams: {
        resourceId: retailerAccountId,
        resourceType: 'retailer_accounts',
      },
    },
  })

  const submitField: CustomFieldFieldProps['submitField'] = useCallback(
    async (config) => {
      if (config.customFieldValueId) {
        const { data } = await updateCustomFieldValue({
          variables: {
            resourceId: config.customFieldValueId,
            data: {
              custom_field_value: {
                value: config.value,
              },
            },
          },
        })
        return data?.updateCustomFieldValue
      } else {
        const { data } = await createCustomFieldValue({
          variables: {
            resourceId: retailerId,
            resourceType: 'retailers',
            data: {
              custom_field_id: config.customFieldId,
              custom_field_value: {
                custom_field_id: config.customFieldId,
                custom_fieldable_id: retailerId,
                custom_fieldable_type: 'Retailer',
                value: config.value,
              },
            },
          },
        })
        return data?.createCustomFieldValue
      }
    },
    [createCustomFieldValue, retailerId, updateCustomFieldValue],
  )

  const fieldSets = useMemo(
    () => data?.getCustomFieldSets || [],
    [data?.getCustomFieldSets],
  )

  const { validationMode, setIsValid } = useManageBrandDialogContext()
  useEffect(() => {
    let isValid = true

    if (validationMode === 'custom_fields') {
      for (const fieldset of fieldSets) {
        for (const field of fieldset.customFields) {
          if (field.required) {
            if (!field.customFieldValues?.value) {
              isValid = false
            }
          }
        }
      }
    }

    setIsValid(isValid)
  }, [fieldSets, setIsValid, validationMode])

  return (
    <div>
      {loading ? (
        <>
          <SkeletonCard />

          <SkeletonCard />
        </>
      ) : null}

      {data ? (
        <Formik
          initialValues={fieldSets}
          onSubmit={async (_sets, { setSubmitting }) => {
            enqueueSnackbar(I18n.t('manageBrands.customFieldsSaved'), {
              variant: 'success',
            })
            setSubmitting(false)
          }}
        >
          {({ isValid, dirty }) => {
            return (
              <Form>
                {isInBusinessSettings ? undefined : (
                  <Box
                    position="sticky"
                    top={
                      isInBusinessSettings
                        ? // When rendered in Business Settings, we need to
                          // account for the top toolbar.
                          // 64px is for Desktop, there's no great way to just
                          // render a <Toolbar /> and magically have the proper
                          // height accounted for.
                          64 + 8
                        : 4
                    }
                    display="flex"
                    justifyContent="flex-end"
                    margin={2}
                  >
                    {/* Disabled buttons in material-ui use transparency, which can
                look a little funky when this button has scrolled down a bit and
                is over other content / borders. Putting it in a container with
                the default background color and some border radius masks those
                instances. */}
                    <Box
                      component="span"
                      bgcolor="background.paper"
                      borderRadius={1}
                    >
                      <Button
                        color="primary"
                        type="submit"
                        variant="contained"
                        disabled={!dirty || (dirty && !isValid)}
                      >
                        {I18n.t('manageBrands.save')}
                      </Button>
                    </Box>
                  </Box>
                )}

                {fieldSets.map((fieldSet, setIndex) =>
                  fieldSet.customFields.length ? (
                    <>
                      <Typography variant="h6">
                        {fieldSet.id === '-1'
                          ? I18n.t('manageBrands.generalQuestions')
                          : fieldSet.heading}
                      </Typography>

                      <Typography color="text.secondary" gutterBottom>
                        {fieldSet.id === '-1'
                          ? I18n.t('manageBrands.generalQuestionsSubText')
                          : fieldSet.help_text}
                      </Typography>

                      <Paper sx={{ marginBottom: 4, padding: 4 }}>
                        {fieldSet.customFields?.map(
                          (customField, fieldIndex) => {
                            if (!customField) {
                              return null
                            }

                            if (customField.retailer_permission === 'hidden') {
                              return null
                            }

                            return (
                              <div>
                                <Field
                                  component={CustomFieldField}
                                  name={`[${setIndex}]customFields[${fieldIndex}]`}
                                  validate={(value: string) =>
                                    validateField({ customField, value })
                                  }
                                  submitField={submitField}
                                />
                              </div>
                            )
                          },
                        )}
                      </Paper>
                    </>
                  ) : null,
                )}
              </Form>
            )
          }}
        </Formik>
      ) : undefined}
    </div>
  )
}

const SkeletonCard: React.FC = () => {
  return (
    <>
      <Typography variant="h6">
        <Skeleton sx={{ width: '40%' }} />
      </Typography>

      <Typography gutterBottom>
        <Skeleton sx={{ width: '25%' }} />
      </Typography>

      <Paper sx={{ marginBottom: 4, padding: 4 }}>
        <Skeleton />
      </Paper>
    </>
  )
}

export default ManageBrandCustomFields

interface validateFieldProps {
  customField: CustomField
  value: string
}
const validateField = ({ customField, value }: validateFieldProps) => {
  if (
    customField.required &&
    customField.retailer_permission === 'write' &&
    (!value || (customField.input_type === 'boolean' && value === 'false'))
  ) {
    return I18n.t('manageBrands.requiredCustomField')
  }
  return
}

const GET_CUSTOM_FIELDS = gql`
  query getCustomFieldSets(
    $params: CustomFieldSetsParams!
    $fieldValueParams: CustomFieldValuesParams!
    $fieldsParams: CustomFieldsParams!
  ) {
    getCustomFieldSets(params: $params) {
      id
      heading
      help_text
      customFields(params: $fieldsParams) {
        id
        choices
        input_type
        display_name
        required
        retailer_permission
        customFieldValues(params: $fieldValueParams) {
          id
          value
        }
      }
    }
  }
`

const UPDATE_CUSTOM_FIELD_VALUE = gql`
  mutation updateCustomFieldValue(
    $data: UpdateCustomFieldValueData!
    $resourceId: ID!
  ) {
    updateCustomFieldValue(data: $data, resourceId: $resourceId) {
      id
      value
    }
  }
`

const CREATE_CUSTOM_FIELD_VALUE = gql`
  mutation createCustomFieldValue(
    $data: CreateCustomFieldValueData!
    $resourceId: ID!
    $resourceType: CreateCustomFieldResourceType!
  ) {
    createCustomFieldValue(
      data: $data
      resourceId: $resourceId
      resourceType: $resourceType
    ) {
      id
      value
    }
  }
`
